import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SummaryApi from "../../components/common/index";
import "./Blog.css";
import { Helmet } from "react-helmet-async";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // --- CREATE A REF FOR THE TOP OF THE PAGE ---
  const topRef = useRef(null);

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6; // Set how many blogs you want per page

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const { url, method } = SummaryApi.getActiveBlog;
        const response = await fetch(url, { method });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();

        if (result.status === true && Array.isArray(result.data)) {
          setBlogs(result.data);
        } else {
          throw new Error(result.message || "Failed to fetch blogs");
        }
      } catch (err) {
        setError(err.message || "Something went wrong while loading blogs");
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // --- PAGINATION LOGIC ---
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);

    // Slight delay ensures React renders the new items before scrolling
    setTimeout(() => {
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" }); // Fallback
      }
    }, 100);
  };

  const getImageUrl = (path) => {
    if (!path) return "https://via.placeholder.com/600x400?text=No+Image";
    if (path.startsWith("http")) return path;
    const baseUrl = SummaryApi.BlogImage.url;
    const cleanPath = path.replace(/^\//, "");
    const cleanBase = baseUrl.replace(/\/$/, "");
    return `${cleanBase}/${cleanPath}`;
  };

  const createSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleViewArticle = (blog) => {
    const slug = createSlug(blog.blog_title);
    navigate(`/blog/${slug}`, { state: { blogData: blog } });
  };

  return (
    // --- ATTACH THE REF HERE ---
    <div className="blog-page" ref={topRef}>
      <Helmet>
        <title>
          Our Blog | Insights on Technology, Marketing & Business | Oakya IT
          Services
        </title>
        <meta
          name="description"
          content="Read the latest blogs from Oakya IT Services on technology, software development, digital marketing, branding, and business growth strategies."
        />
        <meta
          name="keywords"
          content=" Oakya IT Services blog, technology insights, digital marketing tips, software development articles, branding strategies, business growth blogs"
        />
        <meta name="author" content="Oakya IT Services" />
        <meta name="robots" content="index, follow" />
        <meta name="publisher" content="Oakya IT Services" />
        <link rel="canonical" href="https://oakyaitservices.com/our-blog" />
      </Helmet>

      <header className="blog-hero">
        <div className="hero-overlay">
          <span className="hero-badge">Knowledge Hub</span>
          <h1>Tech Pulse</h1>
          <p>Expert insights and industry trends, curated for innovators.</p>
        </div>
      </header>

      <main className="blog-container">
        {loading ? (
          <div className="status-container">
            <div className="spinner"></div>
            <p>Fetching latest insights...</p>
          </div>
        ) : error ? (
          <div className="status-container">
            <div className="error-box">
              <h3>Oops!</h3>
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="blog-grid">
              {currentBlogs.map((blog) => (
                <div
                  key={blog.blog_id}
                  className="blog-card"
                  onClick={() => handleViewArticle(blog)}
                >
                  <div className="card-media">
                    <img
                      className="main-cover"
                      src={getImageUrl(
                        blog.cover_image || blog.blog_thumbnail || "",
                      )}
                      alt={blog.blog_title}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/800x450?text=Image+Not+Found";
                      }}
                    />
                    <div className="thumbnail-accent">
                      <img
                        src={getImageUrl(blog.blog_thumbnail || "")}
                        alt=""
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    </div>
                    <div className="category-tag">Publication</div>
                  </div>

                  <div className="card-content">
                    <div className="meta-info">
                      <span className="post-date">
                        {new Date(blog.post_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <h3 className="blog-title">{blog.blog_title}</h3>
                    <div
                      className="blog-excerpt"
                      dangerouslySetInnerHTML={{ __html: blog.blog_content }}
                    />
                    <div className="card-footer">
                      <div className="tags-wrapper">
                        {blog.tags
                          ?.split(",")
                          .slice(0, 2)
                          .map((tag, index) => (
                            <span key={index} className="tag-pill">
                              #{tag.trim()}
                            </span>
                          ))}
                      </div>
                      <button className="arrow-link">
                        Read Article <span>→</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* --- PAGINATION UI --- */}
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <button
                  className="page-btn arrow"
                  disabled={currentPage === 1}
                  onClick={() => paginate(currentPage - 1)}
                >
                  &larr; Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  className="page-btn arrow"
                  disabled={currentPage === totalPages}
                  onClick={() => paginate(currentPage + 1)}
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Blog;
